import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  Tabs,
  Tab,
  Drawer,
  Fab,
  Snackbar,
  Slider,
  List,
  ListItem,
  ListItemText,
  ListItemButton
} from '@mui/material';
import {
  Edit as EditIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Movie as MovieIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  SkipNext as SkipNextIcon,
  SkipPrevious as SkipPrevIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  ContentCut as SplitIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { voerUitgesteldUit } from '../../utils/uitgesteld';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import PageHeader from '../../ui-components/PageHeader';

// Parse SRT content into array of subtitle objects
function parseSRT(srtContent) {
  if (!srtContent) return [];
  
  const blocks = srtContent.trim().split(/\n\n+/);
  const subtitles = [];
  
  for (const block of blocks) {
    const lines = block.split('\n');
    if (lines.length >= 3) {
      const index = parseInt(lines[0], 10);
      const timeParts = lines[1].match(/(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/);
      if (timeParts) {
        subtitles.push({
          index,
          startTime: timeParts[1],
          endTime: timeParts[2],
          text: lines.slice(2).join('\n'),
          startSeconds: timeToSeconds(timeParts[1]),
          endSeconds: timeToSeconds(timeParts[2])
        });
      }
    }
  }
  
  return subtitles;
}

// Convert timecode to seconds
function timeToSeconds(timeStr) {
  const [time, ms] = timeStr.split(',');
  const [h, m, s] = time.split(':').map(Number);
  return h * 3600 + m * 60 + s + parseInt(ms, 10) / 1000;
}

// Convert seconds to timecode
function secondsToTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 1000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')},${String(ms).padStart(3, '0')}`;
}

// Convert subtitle array back to SRT string
function subtitlesToSRT(subtitles) {
  return subtitles.map((sub, idx) => {
    return `${idx + 1}\n${sub.startTime} --> ${sub.endTime}\n${sub.text}`;
  }).join('\n\n');
}

// Extract Frame.io asset ID from various URL formats
function extractFrameIoAssetId(url) {
  if (!url) return null;
  
  // Pattern: /assets/[asset_id] or /player/[asset_id] or /reviews/[review_id]/[asset_id]
  const patterns = [
    /\/assets\/([a-f0-9-]+)/i,
    /\/player\/([a-f0-9-]+)/i,
    /\/reviews\/[a-f0-9-]+\/([a-f0-9-]+)/i,
    /frame\.io\/[^\/]+\/([a-f0-9-]+)/i
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
}

function SRTDashboard() {
  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const userEmail = activeAccount?.username;
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [layouts, setLayouts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [subtitles, setSubtitles] = useState([]);
  const [selectedSubtitleIndex, setSelectedSubtitleIndex] = useState(null);
  const [selectedLayout, setSelectedLayout] = useState('box');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editorTab, setEditorTab] = useState(0);

  // Video state
  const videoRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeSubtitle, setActiveSubtitle] = useState(null);

  // Audio preview state
  const audioRef = useRef(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);

  // Refs for subtitle list scrolling
  const subtitleRefs = useRef({});

  // Fetch jobs and layouts
  const fetchData = useCallback(async () => {
    if (!userEmail) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [jobsRes, layoutsRes, statsRes] = await Promise.all([
        authFetch(`${AI_ENGINE_URL}/api/v1/srt-jobs/?user_email=${encodeURIComponent(userEmail)}`),
        authFetch(`${AI_ENGINE_URL}/api/v1/srt-jobs/layouts`),
        authFetch(`${AI_ENGINE_URL}/api/v1/srt-jobs/user/${encodeURIComponent(userEmail)}/stats`)
      ]);

      if (jobsRes.ok) {
        const jobsData = await jobsRes.json();
        setJobs(jobsData);
      }

      if (layoutsRes.ok) {
        const layoutsData = await layoutsRes.json();
        setLayouts(layoutsData);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Kon gegevens niet ophalen. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Ctrl+Z / Cmd+Z buiten tekstvelden = laatste actie terugdraaien
  useEffect(() => registreerOngedaanSneltoets(), []);

  // Update active subtitle based on video time
  useEffect(() => {
    if (subtitles.length === 0) return;
    
    const active = subtitles.find(
      sub => currentTime >= sub.startSeconds && currentTime <= sub.endSeconds
    );
    
    if (active && active.index !== activeSubtitle?.index) {
      setActiveSubtitle(active);
      // Scroll to active subtitle
      if (subtitleRefs.current[active.index]) {
        subtitleRefs.current[active.index].scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    } else if (!active && activeSubtitle) {
      setActiveSubtitle(null);
    }
  }, [currentTime, subtitles, activeSubtitle]);

  // Open editor with job
  const handleOpenEditor = (job) => {
    setSelectedJob(job);
    const srtContent = job.corrected_srt || job.original_srt || '';
    setSubtitles(parseSRT(srtContent));
    setSelectedLayout(job.subtitle_style || 'box');
    setSelectedSubtitleIndex(null);
    setCurrentTime(0);
    setIsPlaying(false);
    setEditorTab(0);
    setEditorOpen(true);
  };

  // Close editor
  const handleCloseEditor = () => {
    setEditorOpen(false);
    setSelectedJob(null);
    setSubtitles([]);
    setIsPlaying(false);
  };

  // Save edited SRT
  const handleSave = async () => {
    if (!selectedJob) return;
    
    setSaving(true);
    try {
      const srtContent = subtitlesToSRT(subtitles);
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/srt-jobs/${selectedJob.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          corrected_srt: srtContent,
          subtitle_style: selectedLayout
        })
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Wijzigingen opgeslagen!', severity: 'success' });
        await fetchData();
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Opslaan mislukt. Probeer het opnieuw.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Re-upload to Frame.io
  const handleReupload = async () => {
    if (!selectedJob) return;
    
    // Check if we have a Frame.io source to download from
    if (!selectedJob.frame_io_source_link && !selectedJob.frame_io_upload_link) {
      setSnackbar({ 
        open: true, 
        message: 'Geen Frame.io bron beschikbaar. Genereer opnieuw via de SRT Generator met een Frame.io link.', 
        severity: 'warning' 
      });
      return;
    }
    
    setUploading(true);

    // Uitgesteld: 5 s "Annuleren" voordat de re-upload naar Frame.io echt start.
    const r = await voerUitgesteldUit('Wordt opnieuw naar Frame.io gestuurd…', async () => {
    setSnackbar({ 
      open: true, 
      message: '⏳ Video downloaden en opnieuw verwerken... Dit kan even duren.', 
      severity: 'info' 
    });
    
    try {
      const srtContent = subtitlesToSRT(subtitles);
      
      // First save to database
      await authFetch(`${AI_ENGINE_URL}/api/v1/srt-jobs/${selectedJob.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          corrected_srt: srtContent,
          subtitle_style: selectedLayout
        })
      });
      
      // Then trigger re-processing (backend will download video from Frame.io)
      const response = await authFetch(`${BACKEND_URL}/Api/srt-generator/reprocess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobId: selectedJob.id,
          srtContent: srtContent,
          subtitleStyle: selectedLayout,
          aspectRatio: selectedJob.aspect_ratio,
          email: selectedJob.user_email
        })
      });

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: '⏳ Re-upload gestart! Video wordt op de achtergrond verwerkt.',
          severity: 'success'
        });
        handleCloseEditor();
        await fetchData();
      } else {
        throw new Error(result.error || 'Re-upload failed');
      }
    } catch (err) {
      console.error('Re-upload error:', err);
      setSnackbar({ 
        open: true, 
        message: `❌ Re-upload mislukt: ${err.message}`, 
        severity: 'error' 
      });
    } finally {
      setUploading(false);
    }
    }, { duur: 5000 });
    if (r.geannuleerd) setUploading(false);
  };

  // Update subtitle text
  const handleSubtitleTextChange = (index, newText) => {
    setSubtitles(prev => prev.map((sub, i) => 
      i === index ? { ...sub, text: newText } : sub
    ));
  };

  // Update subtitle timing
  const handleSubtitleTimeChange = (index, field, value) => {
    setSubtitles(prev => prev.map((sub, i) => {
      if (i !== index) return sub;
      const newSub = { ...sub, [field]: value };
      if (field === 'startTime') {
        newSub.startSeconds = timeToSeconds(value);
      } else if (field === 'endTime') {
        newSub.endSeconds = timeToSeconds(value);
      }
      return newSub;
    }));
  };

  // Add new subtitle
  const handleAddSubtitle = () => {
    const lastSub = subtitles[subtitles.length - 1];
    const newStart = lastSub ? lastSub.endSeconds + 0.5 : 0;
    const newEnd = newStart + 3;
    
    setSubtitles(prev => [...prev, {
      index: prev.length + 1,
      startTime: secondsToTime(newStart),
      endTime: secondsToTime(newEnd),
      text: '',
      startSeconds: newStart,
      endSeconds: newEnd
    }]);
  };

  // Delete subtitle
  const handleDeleteSubtitle = (index) => {
    const verwijderd = subtitles[index];
    setSubtitles(prev => prev.filter((_, i) => i !== index).map((sub, i) => ({
      ...sub,
      index: i + 1
    })));
    if (verwijderd) {
      // Ongedaan maken = regel op dezelfde plek terugzetten (lokaal, tot Opslaan)
      biedOngedaanAan('Regel verwijderd', async () => {
        setSubtitles(prev => {
          const next = [...prev];
          next.splice(Math.min(index, next.length), 0, verwijderd);
          return next.map((sub, i) => ({ ...sub, index: i + 1 }));
        });
      });
    }
  };

  // Duplicate subtitle (copy with halved duration)
  const handleDuplicateSubtitle = (index) => {
    const originalSub = subtitles[index];
    const duration = originalSub.endSeconds - originalSub.startSeconds;
    const halfDuration = duration / 2;
    const midpointSeconds = originalSub.startSeconds + halfDuration;
    const midpointTime = secondsToSrtTime(midpointSeconds);
    
    // Original gets first half (keep start, shorten end to midpoint)
    const updatedOriginal = {
      ...originalSub,
      endSeconds: midpointSeconds,
      endTime: midpointTime
    };
    
    // New copy gets second half (start at midpoint, keep original end)
    const newSub = {
      ...originalSub,
      index: originalSub.index + 1,
      startSeconds: midpointSeconds,
      startTime: midpointTime,
      endSeconds: originalSub.endSeconds,
      endTime: originalSub.endTime
    };
    
    // Insert new subtitle after original and re-index all
    setSubtitles(prev => {
      const newList = [...prev];
      newList[index] = updatedOriginal;
      newList.splice(index + 1, 0, newSub);
      return newList.map((sub, i) => ({ ...sub, index: i + 1 }));
    });
    
    // Show notification
    setSnackbar({ 
      open: true, 
      message: `Ondertitel gedupliceerd! Tijd gehalveerd: ${halfDuration.toFixed(1)}s per blok`, 
      severity: 'success' 
    });
  };

  // Split subtitle: split text at natural boundary + redistribute timing proportionally
  const handleSplitSubtitle = (index) => {
    const originalSub = subtitles[index];
    const text = originalSub.text;
    const words = text.split(/\s+/);

    if (words.length < 2) {
      setSnackbar({ open: true, message: 'Te weinig woorden om te splitsen', severity: 'warning' });
      return;
    }

    // Find best split point: sentence end > clause boundary > midpoint
    const midIdx = Math.floor(words.length / 2);
    let splitIdx = midIdx;

    // Try sentence boundary near middle
    for (let i = Math.max(1, midIdx - 2); i <= Math.min(words.length - 2, midIdx + 2); i++) {
      if (/[.!?]$/.test(words[i])) { splitIdx = i + 1; break; }
    }
    // If no sentence boundary, try clause boundary
    if (splitIdx === midIdx) {
      for (let i = Math.max(1, midIdx - 2); i <= Math.min(words.length - 2, midIdx + 2); i++) {
        if (/[,;:]$/.test(words[i])) { splitIdx = i + 1; break; }
      }
    }

    const text1 = words.slice(0, splitIdx).join(' ');
    const text2 = words.slice(splitIdx).join(' ');
    const totalChars = text1.length + text2.length;
    const duration = originalSub.endSeconds - originalSub.startSeconds;
    const ratio = text1.length / totalChars;
    const splitTime = originalSub.startSeconds + duration * ratio;

    const part1 = {
      ...originalSub,
      text: text1,
      endSeconds: splitTime,
      endTime: secondsToSrtTime(splitTime)
    };
    const part2 = {
      ...originalSub,
      index: originalSub.index + 1,
      text: text2,
      startSeconds: splitTime,
      startTime: secondsToSrtTime(splitTime),
      endSeconds: originalSub.endSeconds,
      endTime: originalSub.endTime
    };

    setSubtitles(prev => {
      const newList = [...prev];
      newList[index] = part1;
      newList.splice(index + 1, 0, part2);
      return newList.map((sub, i) => ({ ...sub, index: i + 1 }));
    });

    const dur1 = (duration * ratio).toFixed(1);
    const dur2 = (duration * (1 - ratio)).toFixed(1);
    setSnackbar({
      open: true,
      message: `Ondertitel gesplitst: ${dur1}s + ${dur2}s (proportioneel)`,
      severity: 'success'
    });
  };

  // Helper to convert seconds to SRT time format (00:00:00,000)
  const secondsToSrtTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 1000);
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')},${String(ms).padStart(3, '0')}`;
  };

  // Open Frame.io link in new tab (since embedding is blocked)
  const handleOpenInFrameIo = (subtitle) => {
    if (selectedJob?.frame_io_source_link) {
      window.open(selectedJob.frame_io_source_link, '_blank');
    }
    setSelectedSubtitleIndex(subtitle.index - 1);
  };

  // Video controls
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleSeek = (event, newValue) => {
    if (videoRef.current) {
      videoRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  // Audio preview handlers
  const handleAudioDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && /\.(mp3|wav|mp4|m4a|ogg|webm)$/i.test(file.name)) {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      const url = URL.createObjectURL(file);
      setAudioFile(file);
      setAudioUrl(url);
      setSnackbar({ open: true, message: `Audio geladen: ${file.name}`, severity: 'success' });
    }
  }, [audioUrl]);

  const handleAudioFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      const url = URL.createObjectURL(file);
      setAudioFile(file);
      setAudioUrl(url);
    }
  }, [audioUrl]);

  const handleAudioPlayPause = () => {
    if (!audioRef.current) return;
    if (audioPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setAudioPlaying(!audioPlaying);
  };

  const handleAudioTimeUpdate = useCallback(() => {
    if (!audioRef.current) return;
    const t = audioRef.current.currentTime;
    setAudioCurrentTime(t);
    // Find active subtitle at current audio time
    const active = subtitles.find(s => t >= s.startSeconds && t <= s.endSeconds);
    setActiveSubtitle(active || null);
    // Auto-scroll to active subtitle
    if (active && subtitleRefs.current[active.index]) {
      subtitleRefs.current[active.index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [subtitles]);

  const handleAudioSeekToSubtitle = (subtitle) => {
    if (audioRef.current) {
      audioRef.current.currentTime = subtitle.startSeconds;
      setAudioCurrentTime(subtitle.startSeconds);
      if (!audioPlaying) {
        audioRef.current.play();
        setAudioPlaying(true);
      }
    }
    setSelectedSubtitleIndex(subtitle.index - 1);
  };

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => { if (audioUrl) URL.revokeObjectURL(audioUrl); };
  }, [audioUrl]);

  // Download functions
  const handleDownload = (job, type = 'corrected') => {
    const content = type === 'corrected' ? job.corrected_srt : job.original_srt;
    if (!content) return;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${job.original_filename.replace(/\.[^/.]+$/, '')}_${type}.srt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadFCPXML = (job) => {
    if (!job.fcpxml) return;

    const blob = new Blob([job.fcpxml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${job.original_filename.replace(/\.[^/.]+$/, '')}.fcpxml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Delete job
  const handleDelete = async (jobId) => {
    if (!(await bevestig({ titel: 'Ondertiteling verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    
    try {
      await authFetch(`${AI_ENGINE_URL}/api/v1/srt-jobs/${jobId}`, { method: 'DELETE' });
      await fetchData();
      setSnackbar({ open: true, message: 'Ondertiteling verwijderd', severity: 'info' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Verwijderen mislukt.', severity: 'error' });
    }
  };

  // Status chip
  const getStatusChip = (status) => {
    switch (status) {
      case 'completed':
        return <Chip icon={<CheckCircleIcon />} label="Voltooid" color="success" size="small" />;
      case 'failed':
        return <Chip icon={<ErrorIcon />} label="Mislukt" color="error" size="small" />;
      case 'processing':
        return <Chip icon={<PendingIcon />} label="Bezig..." color="warning" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  // Get Frame.io embed URL
  const getFrameIoEmbedUrl = (job) => {
    const uploadLink = job.frame_io_upload_link;
    const sourceLink = job.frame_io_source_link;
    
    // Try upload link first, then source link
    const assetId = extractFrameIoAssetId(uploadLink) || extractFrameIoAssetId(sourceLink);
    if (assetId) {
      return `https://app.frame.io/player/${assetId}`;
    }
    return null;
  };

  if (!userEmail) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Log in om je ondertitelingen te bekijken.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      {/* Header */}
      <PageHeader
        icon="subtitles"
        title="SRT Beheer"
        subtitle="Bekijk, bewerk en download je gegenereerde ondertitels"
        accent={{ color: '#635983', bg: '#dbcdff' }}
        rightActions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/SRTGenerator')}
            >
              Nieuwe SRT
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
            >
              Vernieuwen
            </Button>
          </Box>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#e3f2fd' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.total_jobs}</Typography>
                <Typography variant="body2">Totaal</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#e8f5e9' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>{stats.completed}</Typography>
                <Typography variant="body2">Voltooid</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#fff3e0' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>{stats.processing}</Typography>
                <Typography variant="body2">Bezig</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#f3e5f5' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.success_rate}%</Typography>
                <Typography variant="body2">Succes rate</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Jobs Table */}
      <Paper elevation={2}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Laden...</Typography>
          </Box>
        ) : jobs.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <MovieIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nog geen ondertitelingen
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ga naar de SRT Generator om je eerste ondertiteling te maken.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => navigate('/SRTGenerator')}
            >
              Maak eerste ondertiteling
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Bestand</strong></TableCell>
                  <TableCell><strong>Datum</strong></TableCell>
                  <TableCell><strong>Taal</strong></TableCell>
                  <TableCell><strong>Stijl</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Acties</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow 
                    key={job.id} 
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => job.status === 'completed' && handleOpenEditor(job)}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {job.original_filename}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {job.frame_io_source_link && (
                            <Chip 
                              label="Bron: Frame.io" 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(job.frame_io_source_link, '_blank');
                              }}
                            />
                          )}
                          {job.frame_io_upload_link && (
                            <Chip 
                              label="📺 Preview" 
                              size="small" 
                              color="primary"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(job.frame_io_upload_link, '_blank');
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(job.created_at), 'd MMM yyyy HH:mm', { locale: nl })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={job.language === 'nl' ? '🇳🇱 NL' : '🇬🇧 EN'} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {layouts[job.subtitle_style]?.name || job.subtitle_style}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(job.status)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Bewerken">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenEditor(job)}
                          disabled={!job.corrected_srt && !job.original_srt}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download SRT">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDownload(job, 'corrected')}
                          disabled={!job.corrected_srt}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download FCP-XML">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDownloadFCPXML(job)}
                          disabled={!job.fcpxml}
                        >
                          <CodeIcon />
                        </IconButton>
                      </Tooltip>
                      {job.frame_io_upload_link && (
                        <Tooltip title="Open in Frame.io">
                          <IconButton 
                            size="small" 
                            onClick={() => window.open(job.frame_io_upload_link, '_blank')}
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Verwijderen">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDelete(job.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Full-Screen Editor Drawer */}
      <Drawer
        anchor="right"
        open={editorOpen}
        onClose={handleCloseEditor}
        PaperProps={{
          sx: { width: '100%', maxWidth: '100vw' }
        }}
      >
        {selectedJob && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Editor Header */}
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: '#1a1a2e',
              color: 'white'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={handleCloseEditor} sx={{ color: 'white' }}>
                  <ArrowBackIcon />
                </IconButton>
                <Box>
                  <Typography variant="h6">
                    ✏️ {selectedJob.original_filename}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {subtitles.length} ondertitels • {layouts[selectedLayout]?.name || selectedLayout}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    const content = subtitlesToSRT(subtitles);
                    const blob = new Blob([content], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedJob.original_filename.replace(/\.[^/.]+$/, '')}.srt`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  SRT
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CodeIcon />}
                  onClick={() => handleDownloadFCPXML(selectedJob)}
                  disabled={!selectedJob.fcpxml}
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  FCP-XML
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || uploading}
                >
                  Opslaan
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                  onClick={handleReupload}
                  disabled={saving || uploading}
                >
                  Opslaan & Re-upload
                </Button>
              </Box>
            </Box>

            {/* Editor Tabs */}
            <Tabs 
              value={editorTab} 
              onChange={(e, v) => setEditorTab(v)}
              sx={{ borderBottom: '1px solid #e0e0e0', px: 2 }}
            >
              <Tab label="🎬 Video & Editor" />
              <Tab label="🎨 Stijl Kiezen" />
              <Tab label="💬 AI Feedback" />
              <Tab label="📝 Raw SRT" />
            </Tabs>

            {/* Tab Content */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              {/* Video & Editor Tab */}
              {editorTab === 0 && (
                <Box sx={{ display: 'flex', height: '100%' }}>
                  {/* Left: Video Preview */}
                  <Box sx={{ 
                    width: '50%', 
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRight: '1px solid #e0e0e0',
                    bgcolor: '#0a0a14'
                  }}>
                    {/* Video Preview Area */}
                    <Box sx={{ 
                      flex: 1, 
                      display: 'flex', 
                      flexDirection: 'column',
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'linear-gradient(145deg, #1a1a2e 0%, #0f0f1a 100%)',
                      background: 'linear-gradient(145deg, #1a1a2e 0%, #0f0f1a 100%)',
                      border: '1px solid rgba(99, 102, 241, 0.2)'
                    }}>
                      {(selectedJob.frame_io_upload_link || selectedJob.frame_io_source_link) ? (
                        <Box sx={{ 
                          flex: 1, 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'center',
                          p: 4,
                          textAlign: 'center'
                        }}>
                          {/* Video thumbnail placeholder */}
                          <Box sx={{
                            width: '100%',
                            maxWidth: 320,
                            aspectRatio: selectedJob.aspect_ratio === '9:16' ? '9/16' : selectedJob.aspect_ratio === '1:1' ? '1/1' : '16/9',
                            bgcolor: 'rgba(99, 102, 241, 0.1)',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px dashed rgba(99, 102, 241, 0.3)',
                            mb: 3,
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            '&:hover': {
                              bgcolor: 'rgba(99, 102, 241, 0.2)',
                              borderColor: 'rgba(99, 102, 241, 0.5)',
                              transform: 'scale(1.02)'
                            }
                          }}
                          onClick={() => window.open(selectedJob.frame_io_upload_link || selectedJob.frame_io_source_link, '_blank')}
                          >
                            <Box sx={{ textAlign: 'center' }}>
                              <PlayIcon sx={{ fontSize: 64, color: '#6366f1', mb: 1 }} />
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Klik om video te openen
                              </Typography>
                            </Box>
                          </Box>

                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3, maxWidth: 280 }}>
                            Open de video in een nieuw venster en plaats deze naast het dashboard voor side-by-side bewerken
                          </Typography>
                          
                          {/* Action buttons */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 280 }}>
                            {selectedJob.frame_io_upload_link && (
                              <Button
                                variant="contained"
                                size="large"
                                startIcon={<PlayIcon />}
                                onClick={() => window.open(selectedJob.frame_io_upload_link, '_blank')}
                                sx={{ 
                                  bgcolor: '#6366f1',
                                  '&:hover': { bgcolor: '#4f46e5' },
                                  textTransform: 'none',
                                  py: 1.5
                                }}
                              >
                                🎬 Video met ondertitels
                              </Button>
                            )}
                            {selectedJob.frame_io_source_link && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<OpenInNewIcon />}
                                onClick={() => window.open(selectedJob.frame_io_source_link, '_blank')}
                                sx={{ 
                                  color: 'rgba(255,255,255,0.8)',
                                  borderColor: 'rgba(255,255,255,0.2)',
                                  '&:hover': { 
                                    borderColor: 'rgba(255,255,255,0.5)',
                                    bgcolor: 'rgba(255,255,255,0.05)'
                                  },
                                  textTransform: 'none'
                                }}
                              >
                                Originele video (zonder ondertitels)
                              </Button>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ 
                          flex: 1, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          p: 4 
                        }}>
                          <Box sx={{ textAlign: 'center', color: 'white', maxWidth: 300 }}>
                            <MovieIcon sx={{ fontSize: 60, color: 'rgba(99, 102, 241, 0.4)', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'rgba(255,255,255,0.7)' }}>
                              Geen video preview
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                              Genereer een nieuwe SRT met "Upload naar Frame.io" aan om de video hier te kunnen openen.
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* Audio Preview Player */}
                    <Box sx={{ mt: 2 }}>
                      {audioUrl ? (
                        <Box sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 1, p: 1.5 }}>
                          <audio
                            ref={audioRef}
                            src={audioUrl}
                            onTimeUpdate={handleAudioTimeUpdate}
                            onLoadedMetadata={() => setAudioDuration(audioRef.current?.duration || 0)}
                            onEnded={() => setAudioPlaying(false)}
                            style={{ display: 'none' }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size="small" onClick={handleAudioPlayPause} sx={{ color: 'white' }}>
                              {audioPlaying ? <PauseIcon /> : <PlayIcon />}
                            </IconButton>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 45, fontFamily: 'monospace' }}>
                              {secondsToTime(audioCurrentTime).substring(3, 12)}
                            </Typography>
                            <Box sx={{ flex: 1, mx: 1 }}>
                              <input
                                type="range"
                                min={0}
                                max={audioDuration || 1}
                                step={0.1}
                                value={audioCurrentTime}
                                onChange={(e) => {
                                  const t = parseFloat(e.target.value);
                                  if (audioRef.current) audioRef.current.currentTime = t;
                                  setAudioCurrentTime(t);
                                }}
                                style={{ width: '100%', cursor: 'pointer' }}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 45, fontFamily: 'monospace' }}>
                              {secondsToTime(audioDuration).substring(3, 12)}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => { setAudioUrl(null); setAudioFile(null); setAudioPlaying(false); }}
                              sx={{ color: 'rgba(255,255,255,0.5)' }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.5 }}>
                            {audioFile?.name} — Klik op een ondertitel om te springen
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={handleAudioDrop}
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.05)',
                            borderRadius: 1,
                            p: 1.5,
                            textAlign: 'center',
                            border: '1px dashed rgba(255,255,255,0.2)',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', borderColor: 'rgba(99,102,241,0.5)' }
                          }}
                          onClick={() => document.getElementById('audio-file-input')?.click()}
                        >
                          <input
                            id="audio-file-input"
                            type="file"
                            accept="audio/*,video/mp4"
                            onChange={handleAudioFileSelect}
                            style={{ display: 'none' }}
                          />
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            Sleep audio/video hier of klik om te laden voor live preview
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Current Subtitle Display */}
                    <Box sx={{
                      mt: 1,
                      p: 2,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      borderRadius: 1,
                      minHeight: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'white',
                          textAlign: 'center',
                          textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                        }}
                      >
                        {activeSubtitle?.text || (
                          <span style={{ opacity: 0.5 }}>Selecteer een ondertitel...</span>
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right: Subtitle Editor */}
                  <Box sx={{ 
                    width: '50%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    bgcolor: '#fafafa'
                  }}>
                    {/* Subtitle List Header */}
                    <Box sx={{ 
                      p: 2, 
                      borderBottom: '1px solid #e0e0e0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Ondertitels ({subtitles.length})
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddSubtitle}
                      >
                        Toevoegen
                      </Button>
                    </Box>

                    {/* Subtitle List */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                      {subtitles.map((subtitle, index) => (
                        <Card 
                          key={index}
                          ref={el => subtitleRefs.current[subtitle.index] = el}
                          sx={{ 
                            mb: 1,
                            border: selectedSubtitleIndex === index 
                              ? '2px solid #1976d2' 
                              : activeSubtitle?.index === subtitle.index
                                ? '2px solid #4caf50'
                                : '1px solid #e0e0e0',
                            bgcolor: activeSubtitle?.index === subtitle.index ? '#e8f5e9' : 'white',
                            transition: 'all 0.2s'
                          }}
                        >
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            {/* Timing Row */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1, 
                              mb: 1 
                            }}>
                              <Chip
                                label={`#${subtitle.index}`}
                                size="small"
                                onClick={() => handleAudioSeekToSubtitle(subtitle)}
                                sx={{ minWidth: 45, cursor: audioUrl ? 'pointer' : 'default' }}
                              />
                              <TextField
                                size="small"
                                value={subtitle.startTime}
                                onChange={(e) => handleSubtitleTimeChange(index, 'startTime', e.target.value)}
                                sx={{ 
                                  width: 130,
                                  '& input': { fontFamily: 'monospace', fontSize: '0.85rem', py: 0.5 }
                                }}
                                placeholder="00:00:00,000"
                              />
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>→</Typography>
                              <TextField
                                size="small"
                                value={subtitle.endTime}
                                onChange={(e) => handleSubtitleTimeChange(index, 'endTime', e.target.value)}
                                sx={{ 
                                  width: 130,
                                  '& input': { fontFamily: 'monospace', fontSize: '0.85rem', py: 0.5 }
                                }}
                                placeholder="00:00:00,000"
                              />
                              <Box sx={{ flex: 1 }} />
                              <Tooltip title="Splits tekst + timing proportioneel">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleSplitSubtitle(index)}
                                >
                                  <SplitIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Dupliceer (tijd wordt gehalveerd)">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleDuplicateSubtitle(index)}
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {selectedJob?.frame_io_source_link && (
                                <Tooltip title="Open in Frame.io">
                                  <IconButton 
                                    size="small"
                                    onClick={() => handleOpenInFrameIo(subtitle)}
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Verwijderen">
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleDeleteSubtitle(index)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>

                            {/* Text Editor */}
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              maxRows={4}
                              value={subtitle.text}
                              onChange={(e) => handleSubtitleTextChange(index, e.target.value)}
                              placeholder="Ondertitel tekst..."
                              onClick={() => setSelectedSubtitleIndex(index)}
                              sx={{ 
                                '& .MuiInputBase-input': { 
                                  fontSize: '0.95rem',
                                  lineHeight: 1.4
                                }
                              }}
                            />
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Style Tab */}
              {editorTab === 1 && (
                <Box sx={{ p: 3, overflow: 'auto', height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Kies een ondertitel stijl</Typography>
                  <Grid container spacing={2}>
                    {Object.entries(layouts).map(([key, layout]) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
                        <Card 
                          sx={{ 
                            cursor: 'pointer',
                            border: selectedLayout === key ? '3px solid #1976d2' : '1px solid #e0e0e0',
                            transition: 'all 0.2s',
                            '&:hover': { boxShadow: 3 }
                          }}
                          onClick={() => setSelectedLayout(key)}
                        >
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {layout.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {layout.description}
                            </Typography>
                            {selectedLayout === key && (
                              <Chip label="✓ Geselecteerd" color="primary" size="small" />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Feedback Tab */}
              {editorTab === 2 && (
                <Box sx={{ p: 3, overflow: 'auto', height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>AI Feedback & Correcties</Typography>
                  <Paper variant="outlined" sx={{ p: 3, bgcolor: '#fafafa' }}>
                    <Typography 
                      variant="body1" 
                      sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
                    >
                      {selectedJob?.feedback || 'Geen feedback beschikbaar voor deze ondertiteling.'}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Raw SRT Tab */}
              {editorTab === 3 && (
                <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Raw SRT Content</Typography>
                    <Button
                      size="small"
                      startIcon={<CopyIcon />}
                      onClick={() => {
                        navigator.clipboard.writeText(subtitlesToSRT(subtitles));
                        setSnackbar({ open: true, message: 'SRT gekopieerd!', severity: 'success' });
                      }}
                    >
                      Kopiëren
                    </Button>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    value={subtitlesToSRT(subtitles)}
                    onChange={(e) => setSubtitles(parseSRT(e.target.value))}
                    sx={{ 
                      flex: 1,
                      '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
                      '& .MuiInputBase-input': { 
                        fontFamily: 'monospace', 
                        fontSize: '0.9rem',
                        height: '100% !important',
                        overflow: 'auto !important'
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SRTDashboard;
